import { Controller } from "@hotwired/stimulus"

// Drives the new/edit feature form: fetches the rule-picker when car
// changes, and resolves (versions × years × regions) selections into a
// concrete set of model_ids that get serialised as hidden inputs.
export default class extends Controller {
  static targets = [
    "carSelect", "rulePicker", "rulePickerBody",
    "pickVersion", "pickYear", "pickRegion",
    "modelCount", "modelList", "modelIdsContainer"
  ]

  static values = {
    prefillModelId:  String,
    pickCarFirst:    { type: String, default: "Pick a car first." },
    moreOverflow:    { type: String, default: "+%{count} more…" }
  }

  connect() {
    if (this.hasCarSelectTarget && this.carSelectTarget.value) {
      this.loadRulePicker()
    } else if (this.hasRulePickerBodyTarget) {
      this.updateSelection()
    }
  }

  carChanged() {
    this.prefillModelIdValue = ""
    this.loadRulePicker()
  }

  loadRulePicker() {
    const carId = this.carSelectTarget.value
    if (!carId) {
      this.rulePickerTarget.innerHTML = `<p class="text-sm text-base-content/40 p-6 text-center">${this._escape(this.pickCarFirstValue)}</p>`
      return
    }
    let url = `/admin/features/rule_picker?car_id=${carId}`
    if (this.prefillModelIdValue) {
      url += `&prefill_model_id=${this.prefillModelIdValue}`
      this.prefillModelIdValue = ""
    }
    fetch(url, {
      headers: { "X-Requested-With": "XMLHttpRequest", "Accept": "text/html" }
    })
      .then(r => r.text())
      .then(html => {
        this.rulePickerTarget.innerHTML = html
        this._excluded = null
        this._seeded = false
        this.updateSelection()
      })
  }

  toggleAll(event) {
    event.preventDefault()
    const group = event.currentTarget.dataset.group
    const targets = this[`${group}Targets`]
    if (!targets) return
    const allChecked = targets.every(el => el.checked)
    targets.forEach(el => { el.checked = !allChecked })
    this.updateSelection()
  }

  // Pairs the user has explicitly unchecked in the resolved list, as
  // "<model_id>:<region_id>". Seeded once per rendered picker:
  //   • editing a feature → whatever the filters resolve to but is NOT
  //     stored becomes an exclusion, so the list mirrors what's saved;
  //   • "Add feature" from a motorization page → every sibling engine in
  //     that bucket starts excluded, so the feature lands on that one only;
  //   • new feature → nothing excluded.
  _seedExclusions(candidates) {
    if (!this._excluded) this._excluded = new Set()
    // Don't let a transient empty resolution (filters still unchecked) count
    // as the seed — that would silently drop a saved selection.
    if (this._seeded || candidates.length === 0) return
    this._seeded = true

    const data = this.rulePickerBodyTarget.dataset
    const prefillId = data.prefillModelId

    if (prefillId) {
      candidates.forEach(m => { if (m.id !== prefillId) this._excluded.add(this._pairKey(m)) })
      return
    }
    if (data.hasStoredSelection !== "true") return

    let stored = []
    try { stored = JSON.parse(data.storedPairs || "[]") } catch { stored = [] }
    const storedSet = new Set(stored)
    candidates.forEach(m => {
      const key = this._pairKey(m)
      if (!storedSet.has(key)) this._excluded.add(key)
    })
  }

  _pairKey(m) {
    return `${m.id}:${m.region_id}`
  }

  // Check / uncheck one resolved motorization.
  toggleModel(event) {
    const key = event.currentTarget.value
    if (!this._excluded) this._excluded = new Set()
    if (event.currentTarget.checked) this._excluded.delete(key)
    else this._excluded.add(key)
    this.updateSelection()
  }

  toggleAllModels(event) {
    event.preventDefault()
    const boxes = this.modelListTarget.querySelectorAll("input[type=checkbox]")
    const allChecked = [ ...boxes ].every(b => b.checked)
    if (!this._excluded) this._excluded = new Set()
    boxes.forEach(b => {
      if (allChecked) this._excluded.add(b.value)
      else this._excluded.delete(b.value)
    })
    this.updateSelection()
  }

  updateSelection() {
    if (!this.hasRulePickerBodyTarget) return
    const raw = this.rulePickerBodyTarget.dataset.models || "[]"
    let models
    try { models = JSON.parse(raw) } catch { models = [] }

    const vIds = this.pickVersionTargets.filter(el => el.checked).map(el => el.value)
    const yIds = this.pickYearTargets.filter(el => el.checked).map(el => el.value)
    const rIds = this.pickRegionTargets.filter(el => el.checked).map(el => el.value)

    // Each facet acts as an independent filter:
    //   • empty group  → no filter on this facet (all candidates pass)
    //   • non-empty    → only models whose facet is in the picked set pass
    // Edge case: ALL groups empty → return 0 so users don't accidentally
    // apply a feature to every model in the catalog with a stray click.
    const allEmpty = vIds.length === 0 && yIds.length === 0 && rIds.length === 0
    const matching = allEmpty ? [] : models.filter(m => {
      if (vIds.length > 0 && !vIds.includes(m.version_id)) return false
      if (yIds.length > 0 && !yIds.includes(String(m.year))) return false
      if (rIds.length > 0 && !rIds.includes(m.region_id))  return false
      return true
    })

    // `matching` holds the candidates the three filters resolve to; the user
    // can then uncheck individual ones. Selection = candidates − excluded.
    this._seedExclusions(matching)
    const included = matching.filter(m => !this._excluded.has(this._pairKey(m)))

    // Motorization count is the distinct models; the saved value keeps the
    // region on each pair.
    const ids = [...new Set(included.map(m => m.id))]
    this.modelCountTarget.textContent = ids.length

    if (this.hasModelListTarget) {
      this.modelListTarget.innerHTML = matching.slice(0, 200).map(m => {
        const key = this._pairKey(m)
        const checked = this._excluded.has(key) ? "" : "checked"
        return `<li><label class="label cursor-pointer justify-start gap-2 py-0.5">` +
          `<input type="checkbox" class="checkbox checkbox-xs" value="${this._escape(key)}" ${checked} ` +
          `data-action="change->feature-form#toggleModel" />` +
          `<span class="label-text text-xs">${this._escape(m.label)}</span></label></li>`
      }).join("")
      if (matching.length > 200) {
        const overflow = this.moreOverflowValue.replace("%{count}", matching.length - 200)
        this.modelListTarget.innerHTML += `<li class="text-xs text-base-content/40">${this._escape(overflow)}</li>`
      }
    }

    // Rewrite hidden inputs as "<model_id>:<region_id>" pairs, so unchecking a
    // region actually removes that region's link even when the motorization is
    // shared with another still-checked region. The input name is data-driven:
    // the home form uses the default "model_region_ids[]", while each "share
    // with other nameplates" picker submits into its own car-scoped bucket.
    const inputName = this.modelIdsContainerTarget.dataset.inputName || "model_region_ids[]"
    const pairs = [...new Set(
      included.filter(m => m.region_id).map(m => this._pairKey(m))
    )]
    this.modelIdsContainerTarget.innerHTML = pairs.map(pair =>
      `<input type="hidden" name="${inputName}" value="${pair}" />`
    ).join("")
  }

  _escape(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }
}
